// frontend/src/components/TagFilterInput.js
// Small autocomplete search box for the genre/tag/studio/meta-tag filter - suggestions are
// scoped to whatever values actually occur in the currently loaded media list (`allItems`).
import React, { useState, useMemo, useRef, useEffect } from 'react';
import { getAllTagValues, getTagLabelMap, countMatching } from '../utils/filterEngine';

const TagFilterInput = ({ allItems, visibleItems, selected, onAdd, onRemove }) => {
  const [query, setQuery] = useState('');
  const [showSuggestions, setShowSuggestions] = useState(false);
  const [highlightedIndex, setHighlightedIndex] = useState(0);
  const inputRef = useRef(null);

  const labelMap = useMemo(() => getTagLabelMap(allItems), [allItems]);
  const allValues = useMemo(() => getAllTagValues(allItems), [allItems]);

  const suggestions = useMemo(() => {
    const q = query.trim().toLowerCase();
    if (!q) return [];
    return allValues.filter((v) => !selected.includes(v) && v.includes(q)).slice(0, 8);
  }, [allValues, query, selected]);

  // Re-anchor the highlight to the top match whenever the suggestion list itself changes.
  useEffect(() => {
    setHighlightedIndex(0);
  }, [suggestions]);

  const addTag = (rawValue) => {
    const value = rawValue.trim().toLowerCase();
    if (!value || selected.includes(value)) return;
    onAdd(value);
    setQuery('');
    setShowSuggestions(false);
    // Re-focus so the user can keep typing to add more tags without re-clicking the box.
    inputRef.current?.focus();
  };

  const handleKeyDown = (e) => {
    if (e.key === 'ArrowDown') {
      e.preventDefault();
      if (suggestions.length === 0) return;
      setShowSuggestions(true);
      setHighlightedIndex((prev) => (prev + 1) % suggestions.length);
    } else if (e.key === 'ArrowUp') {
      e.preventDefault();
      if (suggestions.length === 0) return;
      setShowSuggestions(true);
      setHighlightedIndex((prev) => (prev - 1 + suggestions.length) % suggestions.length);
    } else if (e.key === 'Enter') {
      e.preventDefault();
      const chosen = suggestions.length > 0 ? suggestions[highlightedIndex] ?? suggestions[0] : query;
      addTag(chosen);
    } else if (e.key === 'Backspace' && !query && selected.length > 0) {
      onRemove(selected[selected.length - 1]);
    } else if (e.key === 'Escape') {
      setShowSuggestions(false);
      inputRef.current?.blur();
    }
  };

  return (
    <div className="tag-filter-input">
      {selected.length > 0 && (
        <div className="tag-filter-chips">
          {selected.map((value) => (
            <span className="tag-chip" key={value}>
              {labelMap.get(value) || value}
              <button
                type="button"
                className="tag-chip-remove"
                onClick={() => onRemove(value)}
                aria-label={`Remove ${labelMap.get(value) || value}`}
              >
                ×
              </button>
            </span>
          ))}
        </div>
      )}
      <div className="tag-filter-input-wrapper">
        <input
          ref={inputRef}
          type="text"
          className="input-field tag-filter-text-input"
          placeholder="Search genre, tag, studio..."
          value={query}
          onChange={(e) => {
            setQuery(e.target.value);
            setShowSuggestions(true);
          }}
          onFocus={() => setShowSuggestions(true)}
          onBlur={() => setTimeout(() => setShowSuggestions(false), 150)}
          onKeyDown={handleKeyDown}
          role="combobox"
          aria-expanded={showSuggestions && suggestions.length > 0}
          aria-autocomplete="list"
        />
        {showSuggestions && suggestions.length > 0 && (
          <ul className="tag-filter-suggestions">
            {suggestions.map((value, index) => (
              <li
                key={value}
                className={index === highlightedIndex ? 'active' : ''}
                onMouseDown={() => addTag(value)}
                onMouseEnter={() => setHighlightedIndex(index)}
              >
                <span className="tag-filter-suggestion-label">{labelMap.get(value) || value}</span>
                <span className="filter-count">{countMatching(visibleItems || [], 'genre_tag', value)}</span>
              </li>
            ))}
          </ul>
        )}
      </div>
    </div>
  );
};

export default TagFilterInput;
